import {
  useFetchers,
  useLocation,
  useNavigation,
  type Fetcher,
} from "react-router-dom";

/**
 * Centralizes React Router pending state for loaders, actions, and fetchers.
 *
 * - `showLayoutOverlay`: cross-route only (dim + TopProgressBar). Stage 1.
 * - `showContentFallback`: child route loading (initial entry, hard refresh). Stage 0.
 * - `isTableLoading`: same pathname, query/hash change (filters, pagination). Stage 2.
 */
export function useRouteActivity() {
  const navigation = useNavigation();
  const location = useLocation();
  const fetchers = useFetchers();

  const isNavigating = navigation.state === "loading";
  const isSubmitting = navigation.state === "submitting";
  const pendingLocation = navigation.location;

  const isCrossRoute =
    pendingLocation != null &&
    pendingLocation.pathname !== location.pathname;

  const isQueryOnlyNavigation =
    pendingLocation != null &&
    pendingLocation.pathname === location.pathname &&
    (pendingLocation.search !== location.search ||
      pendingLocation.hash !== location.hash);

  const isFetcherBusy = fetchers.some((fetcher) => fetcher.state !== "idle");

  const showLayoutOverlay = (isNavigating || isSubmitting) && isCrossRoute;

  const showContentFallback =
    isNavigating && !isCrossRoute && !isQueryOnlyNavigation;

  const isTableLoading = isNavigating && !isCrossRoute;

  return {
    isNavigating,
    isSubmitting,
    isCrossRoute,
    isQueryOnlyNavigation,
    isFetcherBusy,
    isPending: isNavigating || isSubmitting || isFetcherBusy,
    isTableLoading,
    isFormBusy: isSubmitting || isFetcherBusy,
    showLayoutOverlay,
    showContentFallback,
  };
}

export function useFetcherBusy(fetcher: Pick<Fetcher, "state">) {
  return fetcher.state !== "idle";
}
